import { useEffect, useState } from 'react';
import i18next from 'i18next';
import { toast } from "react-toastify";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * This component and utility manage face detection using face-api.js.
 * It dynamically loads required scripts, ensures models are ready,
 * and exposes a face validation method that works with both
 * base64 images and Blob/File inputs.
 */

// Utility object to handle face-api.js loading and validation
const faceDetectionManager = {
    loaded: false, // Whether libraries and models are already loaded
    loading: false, // Prevents loading multiple times in parallel

    /**
     * Dynamically load a JavaScript file by creating a <script> tag.
     * @param {string} src - URL of the script to load
     * @returns {Promise<void>}
     */
    loadScript(src) {
        return new Promise((resolve, reject) => {
            // If script already exists, resolve immediately
            if (document.querySelector(`script[src="${src}"]`)) return resolve();

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve; // Resolve when loaded
            script.onerror = reject; // Reject on error
            document.body.appendChild(script);
        });
    },

    /**
     * Wait for `window.faceapi` to become available.
     * Times out after 5 seconds if not found.
     */
    async waitForFaceApi() {
        if (window.faceapi) return; // Already available

        await new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (window.faceapi) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);

            // Timeout after 5 seconds
            setTimeout(() => {
                clearInterval(interval);
                reject(new Error(i18next.t('something_went_wrong_2')));
            }, 5000);
        });
    },

    /**
     * Load TensorFlow.js, face-api.js, and the TinyFaceDetector model.
     */
    async loadLibraries() {
        if (this.loaded || this.loading) return;
        this.loading = true;

        try {
            // Load TensorFlow.js and face-api.js from CDN
            await this.loadScript('/faceApiLibs/tf.min.js'); //
            await this.loadScript('/faceApiLibs/face-api.min.js'); //

            // Wait until face-api is ready
            await this.waitForFaceApi();

            // Load TinyFaceDetector model from public folder
            await window.faceapi.nets.tinyFaceDetector.loadFromUri('/faceApiModels');

            this.loaded = true;
        } catch (err) {
            toast.error(i18next.t('something_went_wrong_2'));
            throw err;
        } finally {
            this.loading = false;
        }
    },

    /**
     * Validate if a face is detected in the given image.
     * @param {string|File|Blob} input - Base64 string or file/blob
     * @returns {Promise<number>} Number of faces detected
     */
    async validateFace(input) {
        if (!this.loaded) await this.loadLibraries();

        let imgDataUrl;

        // If input is already a base64 image string
        if (typeof input === 'string' && input.startsWith('data:image/')) {
            imgDataUrl = input;
        } else {
            // Otherwise, convert file/blob to base64
            imgDataUrl = await this.readFileAsDataURL(input);
        }

        // Create an Image object from the base64 string
        const img = await this.loadImage(imgDataUrl);

        // Detect all faces using TinyFaceDetector
        const detections = await window.faceapi.detectAllFaces(
            img,
            new window.faceapi.TinyFaceDetectorOptions({
                inputSize: 320,       // Resize image before detection
                scoreThreshold: 0.60  // Minimum confidence
            })
        );

        return detections.length;
    },

    /**
     * Convert a file/blob into a base64 DataURL.
     */
    readFileAsDataURL(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    },

    /**
     * Load an image from a base64 or URL into an Image object.
     */
    loadImage(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Avoid CORS issues
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    }
};

// React hook-based component wrapper
const FaceDetectorApp = () => {
    const [modelsLoaded, setModelsLoaded] = useState(false);

    // Load libraries on component mount
    useEffect(() => {
        const load = async () => {
            try {
                await faceDetectionManager.loadLibraries();
                setModelsLoaded(true);
            } catch (err) {
                toast.error(i18next.t('something_went_wrong_2'));
            }
        };
    }, []);

    /**
     * Wrapper method to validate faces, only runs if models are loaded.
     */
    const validateFaceBlobAndBase64 = async (input) => {
        try {
            if (!modelsLoaded) {
                await faceDetectionManager.loadLibraries();
                setModelsLoaded(true);
            }

            return await faceDetectionManager.validateFace(input);
        } catch (err) {
            toast.error(i18next.t('something_went_wrong_2'));
            return 0;
        }
    };

    // Returns an API-like object for external use
    return {
        handleValidateFace: validateFaceBlobAndBase64
    };
};

export default FaceDetectorApp;
